// client/src/components/integrations/TikTokMetrics.jsx
// ========================================================================
// TikTok Metrics/Analytics Component
// Displays user's TikTok videos with engagement metrics
// ========================================================================

import React, { useState, useEffect } from 'react';
import axios from 'axios';

/**
 * TikTok Metrics Component
 * Shows the connected account's recent posts with views, likes, comments, shares
 * @param {number} tenantId - The tenant ID to fetch metrics for
 */
function TikTokMetrics({ tenantId }) {
  const [page, setPage] = useState({
    items: [],
    cursor: 0,
    has_more: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [totalMetrics, setTotalMetrics] = useState({
    totalViews: 0,
    totalLikes: 0,
    totalComments: 0,
    totalShares: 0
  });

  const baseUrl = process.env.REACT_APP_BASE_URL || window.location.origin;

  // Load metrics on mount and when tenantId changes
  useEffect(() => {
    if (tenantId) {
      loadMetrics(0);
    }
  }, [tenantId]);

  /**
   * Load metrics from TikTok API
   * @param {number} cursor - Pagination cursor (0 for first page)
   */
  const loadMetrics = async (cursor = 0) => {
    try {
      setLoading(true);
      setError('');

      const { data } = await axios.post(`${baseUrl}/api/integrations/tiktok/metrics/list`, {
        tenantId,
        cursor,
        page_size: 20
      });

      if (!data.success) {
        throw new Error(data.error || 'Failed to load metrics');
      }

      const apiData = data.data || {};
      const videos = apiData.videos || [];

      setPage({
        items: videos,
        cursor: apiData.cursor || 0,
        has_more: apiData.has_more || false
      });

      // Calculate total metrics
      if (videos.length > 0) {
        const totals = videos.reduce((acc, video) => ({
          totalViews: acc.totalViews + (video.view_count || 0),
          totalLikes: acc.totalLikes + (video.like_count || 0),
          totalComments: acc.totalComments + (video.comment_count || 0),
          totalShares: acc.totalShares + (video.share_count || 0)
        }), {
          totalViews: 0,
          totalLikes: 0,
          totalComments: 0,
          totalShares: 0
        });

        setTotalMetrics(totals);
      }

    } catch (err) {
      console.error('[TikTok] Failed to load metrics:', err);
      
      // Handle specific errors
      if (err.response?.status === 401) {
        setError('TikTok connection expired. Please reconnect your account.');
      } else {
        setError(err.response?.data?.error || err.message || 'Failed to load metrics');
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Format number with commas (e.g., 1000 -> 1,000)
   */
  const formatNumber = (num) => {
    if (num === null || num === undefined) return '-';
    return num.toLocaleString();
  };

  /**
   * Format timestamp to readable date
   */
  const formatDate = (timestamp) => {
    if (!timestamp) return '-';
    return new Date(timestamp * 1000).toLocaleString();
  };

  // Don't render if no tenant
  if (!tenantId) {
    return null;
  }

  return (
    <div className="card">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h5 className="mb-0">TikTok Analytics</h5>
        <button
          className="btn btn-sm btn-outline-primary"
          onClick={() => loadMetrics(0)}
          disabled={loading}
        >
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Loading...
            </>
          ) : (
            'Refresh'
          )}
        </button>
      </div>

      <div className="card-body">
        {/* Error display */}
        {error && (
          <div className="alert alert-danger" role="alert">
            <strong>Error:</strong> {error}
          </div>
        )}

        {/* Loading state */}
        {loading && page.items.length === 0 && (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-2 text-muted">Loading your TikTok analytics...</p>
          </div>
        )}

        {/* No data state */}
        {!loading && !error && page.items.length === 0 && (
          <div className="text-center py-5 text-muted">
            <svg width="64" height="64" fill="currentColor" className="bi bi-bar-chart mb-3" viewBox="0 0 16 16">
              <path d="M4 11H2v3h2v-3zm5-4H7v7h2V7zm5-5v12h-2V2h2zm-2-1a1 1 0 0 0-1 1v12a1 1 0 0 0 1 1h2a1 1 0 0 0 1-1V2a1 1 0 0 0-1-1h-2zM6 7a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V7zm-5 4a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v3a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1v-3z"/>
            </svg>
            <p>No TikTok videos found</p>
            <small>Videos you post will appear here with their metrics</small>
          </div>
        )}

        {/* Summary metrics */}
        {!loading && !error && page.items.length > 0 && (
          <>
            <div className="row mb-4">
              <div className="col-md-3 col-6 mb-2">
                <div className="card bg-light">
                  <div className="card-body text-center">
                    <h6 className="text-muted mb-1">Total Views</h6>
                    <h4 className="mb-0">{formatNumber(totalMetrics.totalViews)}</h4>
                  </div>
                </div>
              </div>
              <div className="col-md-3 col-6 mb-2">
                <div className="card bg-light">
                  <div className="card-body text-center">
                    <h6 className="text-muted mb-1">Total Likes</h6>
                    <h4 className="mb-0">{formatNumber(totalMetrics.totalLikes)}</h4>
                  </div>
                </div>
              </div>
              <div className="col-md-3 col-6 mb-2">
                <div className="card bg-light">
                  <div className="card-body text-center">
                    <h6 className="text-muted mb-1">Total Comments</h6>
                    <h4 className="mb-0">{formatNumber(totalMetrics.totalComments)}</h4>
                  </div>
                </div>
              </div>
              <div className="col-md-3 col-6 mb-2">
                <div className="card bg-light">
                  <div className="card-body text-center">
                    <h6 className="text-muted mb-1">Total Shares</h6>
                    <h4 className="mb-0">{formatNumber(totalMetrics.totalShares)}</h4>
                  </div>
                </div>
              </div>
            </div>

            {/* Video list */}
            <div className="list-group">
              {page.items.map((video) => (
                <div key={video.id} className="list-group-item">
                  <div className="row align-items-center">
                    {/* Video thumbnail */}
                    <div className="col-auto">
                      {video.cover_image_url && (
                        <img
                          src={video.cover_image_url}
                          alt={video.title || 'TikTok video'}
                          style={{
                            width: '72px',
                            height: '128px',
                            objectFit: 'cover',
                            borderRadius: '8px'
                          }}
                        />
                      )}
                    </div>

                    {/* Video info */}
                    <div className="col">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <div>
                          <h6 className="mb-1">
                            {video.title || '(No title)'}
                          </h6>
                          <small className="text-muted">
                            Posted: {formatDate(video.create_time)}
                          </small>
                        </div>
                        {video.share_url && (
                          <a
                            href={video.share_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-sm btn-outline-primary"
                          >
                            View on TikTok
                          </a>
                        )}
                      </div>

                      {/* Metrics */}
                      <div className="row g-2">
                        <div className="col-6 col-md-3">
                          <div className="d-flex align-items-center">
                            <svg width="16" height="16" fill="currentColor" className="bi bi-eye me-1" viewBox="0 0 16 16">
                              <path d="M16 8s-3-5.5-8-5.5S0 8 0 8s3 5.5 8 5.5S16 8 16 8zM1.173 8a13.133 13.133 0 0 1 1.66-2.043C4.12 4.668 5.88 3.5 8 3.5c2.12 0 3.879 1.168 5.168 2.457A13.133 13.133 0 0 1 14.828 8c-.058.087-.122.183-.195.288-.335.48-.83 1.12-1.465 1.755C11.879 11.332 10.119 12.5 8 12.5c-2.12 0-3.879-1.168-5.168-2.457A13.134 13.134 0 0 1 1.172 8z"/>
                              <path d="M8 5.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zM4.5 8a3.5 3.5 0 1 1 7 0 3.5 3.5 0 0 1-7 0z"/>
                            </svg>
                            <span className="small">{formatNumber(video.view_count)}</span>
                          </div>
                        </div>
                        <div className="col-6 col-md-3">
                          <div className="d-flex align-items-center">
                            <svg width="16" height="16" fill="currentColor" className="bi bi-heart-fill text-danger me-1" viewBox="0 0 16 16">
                              <path fillRule="evenodd" d="M8 1.314C12.438-3.248 23.534 4.735 8 15-7.534 4.736 3.562-3.248 8 1.314z"/>
                            </svg>
                            <span className="small">{formatNumber(video.like_count)}</span>
                          </div>
                        </div>
                        <div className="col-6 col-md-3">
                          <div className="d-flex align-items-center">
                            <svg width="16" height="16" fill="currentColor" className="bi bi-chat-fill me-1" viewBox="0 0 16 16">
                              <path d="M8 15c4.418 0 8-3.134 8-7s-3.582-7-8-7-8 3.134-8 7c0 1.76.743 3.37 1.97 4.6-.097 1.016-.417 2.13-.771 2.966-.079.186.074.394.273.362 2.256-.37 3.597-.938 4.18-1.234A9.06 9.06 0 0 0 8 15z"/>
                            </svg>
                            <span className="small">{formatNumber(video.comment_count)}</span>
                          </div>
                        </div>
                        <div className="col-6 col-md-3">
                          <div className="d-flex align-items-center">
                            <svg width="16" height="16" fill="currentColor" className="bi bi-share-fill me-1" viewBox="0 0 16 16">
                              <path d="M11 2.5a2.5 2.5 0 1 1 .603 1.628l-6.718 3.12a2.499 2.499 0 0 1 0 1.504l6.718 3.12a2.5 2.5 0 1 1-.488.876l-6.718-3.12a2.5 2.5 0 1 1 0-3.256l6.718-3.12A2.5 2.5 0 0 1 11 2.5z"/>
                            </svg>
                            <span className="small">{formatNumber(video.share_count)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {page.has_more && (
              <div className="text-center mt-3">
                <button
                  className="btn btn-outline-primary"
                  onClick={() => loadMetrics(page.cursor)}
                  disabled={loading}
                >
                  {loading ? 'Loading...' : 'Load More Videos'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default TikTokMetrics;